import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, BookOpen, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import EbookPageFooter from '../shared/EbookPageFooter';

export default function BasicFeelingsNeeds() {
  const navigate = useNavigate();
  
  const needs = [
    { key: 'food', label: 'Food', exampleMet: 'content, relaxed, calm', exampleUnmet: 'hungry, irritable, weak' },
    { key: 'support', label: 'Support', exampleMet: '', exampleUnmet: '' },
    { key: 'acceptance', label: 'Acceptance', exampleMet: '', exampleUnmet: '' },
    { key: 'belonging', label: 'Belonging', exampleMet: '', exampleUnmet: '' },
    { key: 'friendship', label: 'Friendship', exampleMet: '', exampleUnmet: '' },
    { key: 'to_be_heard', label: 'To be heard', exampleMet: '', exampleUnmet: '' },
    { key: 'to_be_understood', label: 'To be understood', exampleMet: '', exampleUnmet: '' },
    { key: 'beauty', label: 'Beauty', exampleMet: '', exampleUnmet: '' },
    { key: 'self_expression', label: 'Self-expression', exampleMet: '', exampleUnmet: '' },
    { key: 'recreation', label: 'Recreation', exampleMet: '', exampleUnmet: '' },
    { key: 'consideration', label: 'Consideration', exampleMet: '', exampleUnmet: '' },
    { key: 'respect', label: 'Respect', exampleMet: '', exampleUnmet: '' }
  ];

  const [responses, setResponses] = useState(() => {
    const saved = localStorage.getItem('basicFeelingsNeedsExercise');
    if (saved) {
      return JSON.parse(saved);
    }
    return needs.reduce((acc, need) => ({
      ...acc,
      [need.key]: { met: need.exampleMet, unmet: need.exampleUnmet }
    }), {});
  });

  const saveProgress = () => {
    localStorage.setItem('basicFeelingsNeedsExercise', JSON.stringify(responses));
    toast.success('Progress saved!');
  };

  const resetExercise = () => {
    const confirmed = window.confirm('Are you sure you want to reset this exercise? All your responses will be cleared.');
    if (confirmed) {
      const reset = needs.reduce((acc, need) => ({
        ...acc,
        [need.key]: { met: need.exampleMet, unmet: need.exampleUnmet }
      }), {});
      setResponses(reset);
      localStorage.removeItem('basicFeelingsNeedsExercise');
      toast.success('Exercise reset!');
    }
  };

  const updateResponse = (needKey, field, value) => {
    setResponses(prev => ({
      ...prev,
      [needKey]: {
        ...prev[needKey],
        [field]: value
      }
    }));
  };

  useEffect(() => {
    // Auto-save every 30 seconds
    const interval = setInterval(() => {
      localStorage.setItem('basicFeelingsNeedsExercise', JSON.stringify(responses));
    }, 30000);
    return () => clearInterval(interval);
  }, [responses]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Basic Feelings and Needs Exercise</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={resetExercise}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-red-600 text-white hover:bg-red-900/20"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <Button
                onClick={saveProgress}
                variant="outline"
                size="sm"
                className="border-green-600 hover:border-green-500 text-white hover:bg-green-900/20"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Progress
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 15</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Instructions */}
          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-6 mb-8">
            <p className="text-blue-100 leading-relaxed mb-4">
              <span className="font-semibold">According to Marshall Rosenberg:</span> "The purpose of Nonviolent Communication 
              is to create a quality of connection where everyone's needs are met through compassionate giving."
            </p>
            <p className="text-blue-100 leading-relaxed">
              Doing this well requires knowing what we are feeling from moment to moment. And also discerning the needs, 
              met or unmet, that are giving rise to those feelings.
            </p>
            <p className="text-blue-200 mt-4 font-semibold">
              ✍️ In this exercise, write down some of the feelings that occur for you when these needs are met and when they are not met.
            </p>
          </div>

          {/* Auto-save notice */}
          <div className="text-center mb-6">
            <p className="text-slate-400 text-sm">
              💾 Your responses are automatically saved every 30 seconds
            </p>
          </div>

          {/* Exercise Table */}
          <div className="space-y-4">
            {needs.map((need, idx) => (
              <div
                key={need.key}
                className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden"
                data-testid={`need-row-${idx}`}
              >
                {/* Need Label */}
                <div className="bg-slate-800/60 px-6 py-3 border-b border-slate-700/30">
                  <h3 className="text-lg font-semibold text-white">{need.label}</h3>
                </div>

                {/* Input Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
                  {/* When Satisfied */}
                  <div>
                    <label className="text-sm font-medium text-green-300 mb-2 block">
                      ✅ Feelings when {need.label.toLowerCase()} IS satisfied
                    </label>
                    <Textarea
                      value={responses[need.key]?.met || ''}
                      onChange={(e) => updateResponse(need.key, 'met', e.target.value)}
                      placeholder={need.exampleMet || 'e.g., peaceful, grateful, energized...'}
                      className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 focus:border-green-600 min-h-[80px]"
                    />
                  </div>

                  {/* When Not Satisfied */}
                  <div>
                    <label className="text-sm font-medium text-red-300 mb-2 block">
                      ❌ Feelings when {need.label.toLowerCase()} is NOT satisfied
                    </label>
                    <Textarea
                      value={responses[need.key]?.unmet || ''}
                      onChange={(e) => updateResponse(need.key, 'unmet', e.target.value)}
                      placeholder={need.exampleUnmet || 'e.g., frustrated, sad, disconnected...'}
                      className="bg-red-900/20 border-red-700/30 text-white placeholder:text-red-300/40 focus:border-red-600 min-h-[80px]"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Teaching Notes */}
          <div className="mt-12 bg-purple-900/20 border border-purple-700/30 rounded-lg p-8">
            <h3 className="text-xl font-bold text-white mb-4">Teaching Notes</h3>
            <div className="space-y-3 text-slate-200">
              <p className="leading-relaxed">
                <span className="font-semibold">Purpose:</span> This exercise helps students develop their "feelings vocabulary" 
                and recognize the direct connection between needs and emotions.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold">Tip:</span> Encourage students to be specific and personal. 
                There are no "right" answers—everyone experiences needs differently.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold">In Class:</span> Have students share one or two of their responses in pairs. 
                Notice how different people may feel different emotions for the same unmet need.
              </p>
              <p className="leading-relaxed mt-4 text-slate-300 italic">
                This awareness is the foundation of self-empathy and emotional intelligence in NVC practice.
              </p>
            </div>
          </div>

          {/* Practice Scenario & Contact Form */}
          <EbookPageFooter 
            pageContext="Basic Feelings & Needs"
            pageNumber={16}
            showScenario={true}
            showContactForm={true}
          />
        </div>
      </div>
    </div>
  );
}
